import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import 'fixed-data-table-2/dist/fixed-data-table-base.css';
import { RadioButton } from 'lego-on-react';
import * as React from 'react';

import { SelectionWizardListName } from '../../actions/phrasesSelectorWizard';
import { PhrasesSelectorWizardInputContainer } from '../../containers/PhrasesSelectorWizardInputContainer/PhrasesSelectorWizardInputContainer';
import {
    mapStringToPhrasesSelectorListItem,
    splitOneSelectorListItemToSeveral
} from '../../mappers/phrasesSelectorWizard';
import { Bem } from '../../service/Bem/Bem';
import { validateCpmBannerPhraseBid } from '../../service/PhraseMoneyCell/PhraseMoneyCell';
import { parseManualPhrases } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { processSelection } from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { KeyCode, ValidationErrorData } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    PhrasesSelectorColumnsVisibilityData,
    PhrasesSelectorInputColumns,
    PhrasesSelectorListItem,
    PhrasesSelectorWizardId
} from '../../typings/phrases';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { HintList } from '../HintList/HintList';
import { Loader, LoaderTheme } from '../Loader/Loader';
import { PhrasesSelectorContextPriceEditor } from '../PhrasesSelectorContextPriceEditor/PhrasesSelectorContextPriceEditor';
import { ScreenErrorMessage } from '../ScreenErrorMessage/ScreenErrorMessage';
import { SelectionList } from '../SelectionList/SelectionList';
import { StartPhraseCell } from '../SelectionList/StartPhraseCell/StartPhraseCell';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './PhrasesSelectorWizard.css';
import * as keyset from './PhrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

export interface HintItem {
    label: string;
    text: string;
}

export interface PhrasesSelectorWizardProps {
    cls?: string;
    isVisible: boolean;
    isInProgress: boolean;

    hintItems: HintItem[];

    columnsVisibility: {
        [SelectionWizardListName.similarPhrases]: boolean;
        [SelectionWizardListName.chosenPhrases]: boolean;
    };
    wizardId: PhrasesSelectorWizardId;

    startPhrases: PhrasesSelectorListItem[];
    similarPhrases: PhrasesSelectorListItem[];
    chosenPhrases: PhrasesSelectorListItem[];
    searchedAlsoPhrases: PhrasesSelectorListItem[];
    minusWordsList: PhrasesSelectorListItem[];
    isMinusWordsListEnabled: boolean;

    isPriceContextEnabled?: boolean;
    priceContext?: number;
    workCurrency: CurrencyCode;
    constants?: ConstData;

    onStartPhrasesSubmit: (value: string[]) => void;
    onSelectionChange: (selection: boolean[], name: SelectionWizardListName) => void;
    onSubmit: () => void;
    deleteStartPhrase: (item: PhrasesSelectorListItem) => void;
    addPhraseToChosenList: (item: PhrasesSelectorListItem[]) => void;
    removePhraseFromChosenList: (item: PhrasesSelectorListItem) => void;
    addPhraseToStartPhrasesList: (item: PhrasesSelectorListItem) => void;
    phrasesSelectorSendMinusPhraseData: (items: PhrasesSelectorListItem[]) => void;
    removePhraseFromMinusWordsList: (item: PhrasesSelectorListItem) => void;
    changeColumnsVisibility: (value: PhrasesSelectorColumnsVisibilityData) => void;
    changePriceContext: (value: number) => void;
}

interface PhrasesSelectorWizardState {
    priceContextChanged: boolean;
    priceContextError?: ValidationErrorData;
}

export class PhrasesSelectorWizard extends React.PureComponent<PhrasesSelectorWizardProps, PhrasesSelectorWizardState> {
    state: PhrasesSelectorWizardState = {
        priceContextChanged: false
    };

    componentWillReceiveProps(props: PhrasesSelectorWizardProps) {
        const { isVisible } = this.props;

        if (props.isVisible !== isVisible) {
            this.setState({
                priceContextChanged: false,
                priceContextError: undefined
            });
        }
    }

    render() {
        const { cls } = this.props;

        return (
            <div className={classNames('phrases-selector-wizard', cls)}>
                <div className={Bem.cls('phrases-selector-wizard', 'header')}>
                    <div className={Bem.cls('phrases-selector-wizard', 'header-title')}>{i18n('phrase-selection')}</div>
                    <div className={Bem.cls('phrases-selector-wizard', 'header-content')}>{this.getHint()}</div>
                </div>
                <div className={Bem.cls('phrases-selector-wizard', 'content')}>
                    {this.getContent()}
                    {this.getSpinner()}
                </div>
            </div>
        );
    }

    private getContent() {
        const { isPriceContextEnabled, wizardId } = this.props;

        return (
            <>
                <div className={Bem.cls('phrases-selector-wizard', 'phrases-input-panel')}>
                    <PhrasesSelectorWizardInputContainer
                        cls={Bem.cls('phrases-selector-wizard', 'phrases-input-wrapper')}
                        onSubmit={this.onStartPhrasesSubmit}
                        wizardId={wizardId}
                        type={PhrasesSelectorInputColumns.SEARCH_VALUE}
                        placeholder={i18n('phrases-input-placeholder')}
                        buttonText={i18n('add-to-start')}
                        buttonSize={'n'}
                        inputSize={'n'}
                    />
                </div>

                <div className={Bem.cls('phrases-selector-wizard', 'columns')}>{this.getColumns()}</div>

                <div className={Bem.cls('phrases-selector-wizard', 'footer')}>
                    {isPriceContextEnabled && this.getPriceContextInput()}
                    <ButtonDecorator
                        disabled={this.isSubmitButtonDisabled()}
                        cls={Bem.cls('phrases-selector-wizard', 'submit')}
                        onClick={this.onSubmit}
                        theme={'action'}
                        size={'n'}
                        text={i18n('add-phrases-to-group')}
                    />
                </div>
            </>
        );
    }

    private isSubmitButtonDisabled() {
        const { chosenPhrases, minusWordsList, isPriceContextEnabled } = this.props;
        const { priceContextChanged, priceContextError } = this.state;

        let isSubmitButtonDisabled = chosenPhrases.length === 0 && minusWordsList.length === 0;

        if (isPriceContextEnabled && !isSubmitButtonDisabled) {
            isSubmitButtonDisabled = !!priceContextError || !priceContextChanged;
        }

        return isSubmitButtonDisabled;
    }

    private getPriceContextInput() {
        const { priceContext, workCurrency } = this.props;
        const { priceContextError } = this.state;

        return (
            <PhrasesSelectorContextPriceEditor
                priceContext={priceContext}
                currency={workCurrency}
                error={priceContextError}
                onChange={this.onPriceContextChange}
                onValueChange={this.onPriceContextValueChange}
            />
        );
    }

    private onPriceContextChange = (value: string) => {
        this.validatePriceContext(value);
    };

    private onPriceContextValueChange = (value: number) => {
        if (value && !this.state.priceContextChanged) {
            this.setState({ priceContextChanged: true });
        }

        this.props.changePriceContext(value);
    };

    private validatePriceContext(priceContext: string) {
        const { constants, workCurrency } = this.props;

        if (!constants) {
            this.setState({ priceContextError: undefined });
        } else {
            const priceLimit = constants.currencyConstants[workCurrency];

            const clientValidationResult = validateCpmBannerPhraseBid(priceContext, priceLimit, workCurrency);

            if (clientValidationResult.errors) {
                this.setState({
                    priceContextError: clientValidationResult.errors[0]
                });
            } else {
                this.setState({ priceContextError: undefined });
            }
        }
    }

    private getSpinner() {
        const { isInProgress, startPhrases } = this.props;

        if (!isInProgress) {
            return null;
        }

        return <Loader size={'l'} theme={!startPhrases.length ? LoaderTheme.white : undefined} />;
    }

    private getColumns() {
        const { columnsVisibility, changeColumnsVisibility, isMinusWordsListEnabled } = this.props;

        const showSimilarPhrasesList = columnsVisibility[SelectionWizardListName.similarPhrases];
        const showChosenPhrasesList = columnsVisibility[SelectionWizardListName.chosenPhrases];

        return (
            <>
                <div className={Bem.cls('phrases-selector-wizard', 'column')}>
                    <div className={Bem.cls('phrases-selector-wizard', 'column-header')}>
                        <span className={Bem.cls('phrases-selector-wizard', 'start-phrases-header')}>
                            {i18n('start-phrases')}
                        </span>
                    </div>
                    {this.getStartPhrasesList()}
                </div>

                <div className={Bem.cls('phrases-selector-wizard', 'column')}>
                    <div className={Bem.cls('phrases-selector-wizard', 'column-header')}>
                        <RadioButton
                            cls={Bem.cls('phrases-selector-wizard', 'list-switcher')}
                            size={'s'}
                            view={'classic'}
                            theme={'normal'}
                            name={'similarPhrasesVisibility'}
                            value={
                                showSimilarPhrasesList
                                    ? SelectionWizardListName.similarPhrases
                                    : SelectionWizardListName.searchedAlso
                            }
                            onChange={value =>
                                changeColumnsVisibility({
                                    [SelectionWizardListName.similarPhrases]:
                                        value.target.value === SelectionWizardListName.similarPhrases
                                })
                            }
                        >
                            <RadioButton.Radio
                                controlAttrs={{
                                    title: i18n('searched-with')
                                }}
                                value={SelectionWizardListName.similarPhrases}
                            >
                                {i18n('searched-with')}
                            </RadioButton.Radio>

                            <RadioButton.Radio
                                controlAttrs={{
                                    title: i18n('searched-also')
                                }}
                                value={SelectionWizardListName.searchedAlso}
                            >
                                {i18n('searched-also')}
                            </RadioButton.Radio>
                        </RadioButton>

                        <span className={Bem.cls('phrases-selector-wizard', 'shows-per-month-header')}>
                            {i18n('shows-per-month')}
                        </span>
                    </div>

                    {showSimilarPhrasesList ? this.getSimilarPhrasesList() : this.getSearchedAlsoPhrasesList()}
                </div>

                <div className={Bem.cls('phrases-selector-wizard', 'column')}>
                    <div className={Bem.cls('phrases-selector-wizard', 'column-header')}>
                        {isMinusWordsListEnabled ? this.getResultColumnViewSwitcher() : i18n('phrases')}
                    </div>

                    {showChosenPhrasesList ? this.getChosenPhrasesList() : this.getMinusWordsList()}
                </div>
            </>
        );
    }

    private getResultColumnViewSwitcher = () => {
        const { columnsVisibility, changeColumnsVisibility } = this.props;

        const showChosenPhrasesList = columnsVisibility[SelectionWizardListName.chosenPhrases];

        return (
            <RadioButton
                cls={Bem.cls('phrases-selector-wizard', 'list-switcher')}
                size={'s'}
                view={'classic'}
                theme={'normal'}
                name={'chosenPhrasesVisibility'}
                value={
                    showChosenPhrasesList ? SelectionWizardListName.chosenPhrases : SelectionWizardListName.minusWords
                }
                onChange={value =>
                    changeColumnsVisibility({
                        [SelectionWizardListName.chosenPhrases]:
                            value.target.value === SelectionWizardListName.chosenPhrases
                    })
                }
            >
                <RadioButton.Radio
                    controlAttrs={{ title: i18n('phrases') }}
                    value={SelectionWizardListName.chosenPhrases}
                >
                    {i18n('phrases')}
                </RadioButton.Radio>

                <RadioButton.Radio
                    controlAttrs={{
                        title: i18n('minus-phrases')
                    }}
                    value={SelectionWizardListName.minusWords}
                >
                    {i18n('minus-phrases')}
                </RadioButton.Radio>
            </RadioButton>
        );
    };

    private getStartPhrasesList() {
        const { startPhrases, deleteStartPhrase } = this.props;

        const startListColumns = [
            {
                getCellContent: (rowIndex: number) => {
                    const item = startPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            text={item.text}
                            key={rowIndex}
                            mods={{
                                'icon-position': 'right'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.trashbin}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => deleteStartPhrase(item)}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            }
        ];

        return startPhrases.length ? (
            <SelectionList
                cls={classNames(
                    Bem.cls('phrases-selector-wizard', 'selection-list'),
                    Bem.cls('phrases-selector-wizard', 'start-phrases-list')
                )}
                columns={startListColumns}
                onSelectionChange={this.onStartPhrasesListSelectonChange}
                rowClassNameGetter={this.getPhrasesSelectorWizardStartPhrasesListItemClass}
                rowsCount={startPhrases.length}
                rowHeight={37}
            />
        ) : (
            <div
                className={Bem.cls('phrases-selector-wizard', 'selection-list', {
                    empty: !startPhrases.length
                })}
            >
                <ScreenErrorMessage
                    cls={Bem.cls('phrases-selector-wizard', 'screen-error-message')}
                    header={i18n('start-phrases-empty-header')}
                    text={i18n('start-phrases-empty-text')}
                />
            </div>
        );
    }

    private getSimilarPhrasesList() {
        const {
            similarPhrases,
            addPhraseToChosenList,
            phrasesSelectorSendMinusPhraseData,
            addPhraseToStartPhrasesList,
            startPhrases,
            isMinusWordsListEnabled
        } = this.props;

        const similarPhrasesColumns = [
            {
                getCellContent: (rowIndex: number) => {
                    const item = similarPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            key={rowIndex}
                            text={item.text}
                            mods={{
                                'icon-position': 'left'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.leftArrow}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => addPhraseToStartPhrasesList(item)}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            },
            {
                getCellContent: (rowIndex: number) => {
                    const item = similarPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            key={rowIndex}
                            text={String(item.shows)}
                            mods={{
                                'text-align': 'right'
                            }}
                        >
                            <Shape2
                                cls={classNames('plus-shape', Bem.cls('phrases-selector-wizard', 'action-icon'))}
                                hoverable={true}
                                icon={Shape2Icon.plus}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => addPhraseToChosenList([item])}
                            />

                            {isMinusWordsListEnabled ? (
                                <Shape2
                                    cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                    hoverable={true}
                                    icon={Shape2Icon.minus}
                                    width={16}
                                    height={16}
                                    theme={Shape2Theme.light}
                                    onClick={() =>
                                        phrasesSelectorSendMinusPhraseData(splitOneSelectorListItemToSeveral(item))
                                    }
                                />
                            ) : null}
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            }
        ];

        return similarPhrases.length ? (
            <SelectionList
                cls={classNames(
                    Bem.cls('phrases-selector-wizard', 'selection-list'),
                    Bem.cls('phrases-selector-wizard', 'similar-phrases-list')
                )}
                columns={similarPhrasesColumns}
                rowClassNameGetter={this.getPhrasesSelectorWizardSimilarPhrasesListItemClass}
                rowsCount={similarPhrases.length}
                rowHeight={37}
            />
        ) : (
            <div
                className={Bem.cls('phrases-selector-wizard', 'selection-list', {
                    empty: !similarPhrases.length
                })}
            >
                <ScreenErrorMessage
                    cls={Bem.cls('phrases-selector-wizard', 'screen-error-message')}
                    header={
                        startPhrases.length
                            ? i18n('similar-phrases-not-exist-header')
                            : i18n('similar-phrases-empty-header')
                    }
                    text={
                        startPhrases.length
                            ? i18n('similar-phrases-not-exist-text')
                            : i18n('similar-phrases-empty-text')
                    }
                />
            </div>
        );
    }

    private getSearchedAlsoPhrasesList() {
        const {
            searchedAlsoPhrases,
            addPhraseToStartPhrasesList,
            addPhraseToChosenList,
            phrasesSelectorSendMinusPhraseData,
            startPhrases
        } = this.props;

        const searchedAlsoListColumns = [
            {
                getCellContent: (rowIndex: number) => {
                    const item = searchedAlsoPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            key={rowIndex}
                            text={item.text}
                            mods={{
                                'icon-position': 'left'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.leftArrow}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => addPhraseToStartPhrasesList(item)}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            },
            {
                getCellContent: (rowIndex: number) => {
                    const item = searchedAlsoPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            key={rowIndex}
                            text={String(item.shows)}
                            mods={{
                                'text-align': 'right'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.plus}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => addPhraseToChosenList([item])}
                            />
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.minus}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => phrasesSelectorSendMinusPhraseData([item])}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            }
        ];

        return searchedAlsoPhrases.length ? (
            <SelectionList
                cls={classNames(
                    Bem.cls('phrases-selector-wizard', 'selection-list'),
                    Bem.cls('phrases-selector-wizard', 'searched-also-phrases-list')
                )}
                columns={searchedAlsoListColumns}
                rowClassNameGetter={this.getPhrasesSelectorWizardSearchedAlsoPhrasesListItemClass}
                rowsCount={searchedAlsoPhrases.length}
                rowHeight={37}
            />
        ) : (
            <div
                className={Bem.cls('phrases-selector-wizard', 'selection-list', {
                    empty: !searchedAlsoPhrases.length
                })}
            >
                <ScreenErrorMessage
                    cls={Bem.cls('phrases-selector-wizard', 'screen-error-message')}
                    header={
                        startPhrases.length
                            ? i18n('search-also-phrases-not-exist-header')
                            : i18n('search-also-phrases-empty-header')
                    }
                    text={
                        startPhrases.length
                            ? i18n('search-also-phrases-not-exist-text')
                            : i18n('search-also-phrases-empty-text')
                    }
                />
            </div>
        );
    }

    private getChosenPhrasesList() {
        const { chosenPhrases, removePhraseFromChosenList, wizardId } = this.props;

        const chosenPhrasesColumns = [
            {
                getCellContent: (rowIndex: number) => {
                    const item = chosenPhrases[rowIndex];

                    return (
                        <StartPhraseCell
                            text={item.text}
                            key={rowIndex}
                            mods={{
                                'icon-position': 'right'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.trashbin}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => removePhraseFromChosenList(item)}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            }
        ];

        return (
            <>
                <PhrasesSelectorWizardInputContainer
                    cls={Bem.cls('phrases-selector-wizard', 'word-text-input')}
                    wizardId={wizardId}
                    type={PhrasesSelectorInputColumns.CHOSEN_WORDS}
                    inputSize={'s'}
                    buttonSize={'s'}
                    placeholder={i18n('phrases')}
                    buttonText={i18n('add-phrases')}
                    onSubmit={this.onWordInputSubmit}
                />

                {chosenPhrases.length ? (
                    <SelectionList
                        cls={classNames(
                            Bem.cls('phrases-selector-wizard', 'selection-list'),
                            Bem.cls('phrases-selector-wizard', 'chosen-phrases-list')
                        )}
                        columns={chosenPhrasesColumns}
                        rowClassNameGetter={this.getPhrasesSelectorWizardChosenPhrasesListItemClass}
                        rowsCount={chosenPhrases.length}
                        rowHeight={37}
                    />
                ) : (
                    <div className={Bem.cls('phrases-selector-wizard', 'selection-list')}>
                        <ScreenErrorMessage
                            cls={Bem.cls('phrases-selector-wizard', 'screen-error-message')}
                            header={i18n('chosen-phrases-empty-header')}
                            text={i18n('chosen-phrases-empty-text')}
                        />
                    </div>
                )}
            </>
        );
    }

    private getMinusWordsList() {
        const { minusWordsList, removePhraseFromMinusWordsList, wizardId } = this.props;

        const minusWordsColumns = [
            {
                getCellContent: (rowIndex: number) => {
                    const item = minusWordsList[rowIndex];

                    return (
                        <StartPhraseCell
                            text={item.text}
                            key={rowIndex}
                            mods={{
                                'icon-position': 'right'
                            }}
                        >
                            <Shape2
                                cls={Bem.cls('phrases-selector-wizard', 'action-icon')}
                                hoverable={true}
                                icon={Shape2Icon.trashbin}
                                width={16}
                                height={16}
                                theme={Shape2Theme.light}
                                onClick={() => removePhraseFromMinusWordsList(item)}
                            />
                        </StartPhraseCell>
                    );
                },
                flexGrow: 1
            }
        ];

        return (
            <>
                <PhrasesSelectorWizardInputContainer
                    cls={Bem.cls('phrases-selector-wizard', 'word-text-input')}
                    wizardId={wizardId}
                    type={PhrasesSelectorInputColumns.MINUS_WORDS}
                    inputSize={'s'}
                    buttonSize={'s'}
                    placeholder={i18n('minus-phrases')}
                    buttonText={i18n('add-minus-phrases')}
                    onSubmit={this.onWordInputSubmit}
                />

                {minusWordsList.length ? (
                    <SelectionList
                        cls={classNames(
                            Bem.cls('phrases-selector-wizard', 'selection-list'),
                            Bem.cls('phrases-selector-wizard', 'minus-words-list')
                        )}
                        columns={minusWordsColumns}
                        rowClassNameGetter={this.getPhrasesSelectorWizardMinusWordsListItemClass}
                        rowsCount={minusWordsList.length}
                        rowHeight={37}
                    />
                ) : (
                    <ScreenErrorMessage
                        cls={Bem.cls('phrases-selector-wizard', 'screen-error-message')}
                        header={i18n('minus-phrases-empty-header')}
                        text={i18n('minus-phrases-empty-text')}
                    />
                )}
            </>
        );
    }

    private getPhrasesSelectorWizardStartPhrasesListItemClass = (index: number) => {
        const { startPhrases } = this.props;

        return Bem.cls('phrases-selector-wizard', 'list-item', {
            selected: startPhrases[index].selected
        });
    };

    private getPhrasesSelectorWizardSimilarPhrasesListItemClass = (index: number) => {
        const { similarPhrases } = this.props;

        return Bem.cls('phrases-selector-wizard', 'list-item', {
            selected: similarPhrases[index].selected
        });
    };

    private getPhrasesSelectorWizardSearchedAlsoPhrasesListItemClass = (index: number) => {
        const { searchedAlsoPhrases } = this.props;

        return Bem.cls('phrases-selector-wizard', 'list-item', {
            selected: searchedAlsoPhrases[index].selected
        });
    };

    private getPhrasesSelectorWizardChosenPhrasesListItemClass = (index: number) => {
        const { chosenPhrases } = this.props;

        return Bem.cls('phrases-selector-wizard', 'list-item', {
            selected: chosenPhrases[index].selected
        });
    };

    private getPhrasesSelectorWizardMinusWordsListItemClass = (index: number) => {
        const { minusWordsList } = this.props;

        return Bem.cls('phrases-selector-wizard', 'list-item', {
            selected: minusWordsList[index].selected
        });
    };

    private onStartPhrasesSubmit = (value: string) => {
        const submitValue = parseManualPhrases(value);

        if (submitValue.length) {
            this.props.onStartPhrasesSubmit(submitValue);
        }
    };

    private onWordInputSubmit = (value: string) => {
        const { phrasesSelectorSendMinusPhraseData, columnsVisibility, addPhraseToChosenList } = this.props;

        const showChosenPhrasesList = columnsVisibility[SelectionWizardListName.chosenPhrases];

        const submitValue = value
            .split(',')
            .map(el => el.trim())
            .filter(el => el)
            .map(mapStringToPhrasesSelectorListItem);

        if (showChosenPhrasesList) {
            addPhraseToChosenList(submitValue);
        } else {
            phrasesSelectorSendMinusPhraseData(submitValue);
        }
    };

    private onStartPhrasesListSelectonChange = (
        indexOfClick: number,
        indexOfPreviousClick: number,
        keyCode?: KeyCode
    ) => {
        const { startPhrases } = this.props;

        const selection = processSelection(startPhrases, indexOfClick, indexOfPreviousClick, keyCode);

        this.props.onSelectionChange(selection, SelectionWizardListName.startPhrases);
    };

    private getHint() {
        const { hintItems } = this.props;

        return <HintList items={hintItems} />;
    }

    private onSubmit = () => {
        this.props.onSubmit();
    };
}
